import React, { memo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { create } from 'store/modules/fetch';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { Theme } from '@app/theme';
import {
  CenteredModal,
  Input,
  SubmitButton,
  Select,
  useToast,
  ErrorMsg,
} from 'components';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'store';
import { useMe } from '@app/api';

enum ColumnType {
  TEXT = 'TEXT',
  EMAIL = 'EMAIL',
  TELEPHONE = 'TELEPHONE',
  DATE = 'DATE',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  TAGS = 'TAGS',
}

const columnsTypeList: ColumnType[] = Object.values(ColumnType);

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.marginBase,
  },
  submitButton: {
    textAlign: 'right',
  },
  infoBox: {
    ...theme.fonts.caption,
    display: 'flex',
    backgroundColor: theme.colors.blueMuted,
    padding: theme.marginBase * 2,
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 4,
  },
  infoIcon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.blue,
    marginRight: theme.marginBase,
  },
  titleInput: {
    marginBottom: theme.marginBase * 2,
  },
  choicesInput: {
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  panelId?: string;
}

const initialValues = {
  title: '',
  type: '',
};

export const CustomColumnModal = memo(
  ({ isOpen, onRequestClose, panelId }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });
    const toast = useToast();

    const { t } = useTranslation();

    const validationSchema = Yup.object().shape({
      title: Yup.string().required().max(25),
      type: Yup.string().oneOf(columnsTypeList).required(),
      choices: Yup.string().when('type', {
        is: (val: ColumnType) =>
          val === ColumnType.TAGS || val === ColumnType.SINGLE_CHOICE,
        then: (schema) =>
          schema.required().matches(/,/, {
            message: t('participants.list.customColumn.errors.choices'),
          }),
      }),
    });

    const dispatch = useDispatch<ThunkDispatch>();
    const { data: account } = useMe();

    const submit = async (values: any) => {
      try {
        await dispatch(
          create({
            payload: {
              message: `${account && account.firstName} ${
                account && account.lastName
              } a demandé à ce qu'on lui ajoute une colonne custom. ~~~~ Informations sur la colonne : Nom : ${
                values.title
              } - Type : ${values.type} - ID du panel : ${panelId} ${
                values.choices ? `- Choix : ${values.choices}` : ''
              }. Merci boss :D`,
            },
            entity: 'NOTIFICATION',
            path: '/notify',
          }),
        );
        toast.success(t('participants.list.customColumn.onClose'));
        onRequestClose();
      } catch (e) {
        toast.exception(e);
      }
    };

    return (
      <CenteredModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={t('participants.list.customColumn.title')}
        id="custom_column"
      >
        <div className={classes.container}>
          <Formik
            onSubmit={submit}
            initialValues={initialValues}
            validationSchema={validationSchema}
          >
            {({ values }) => (
              <Form>
                <Input
                  label={t('participants.list.customColumn.form.title')}
                  placeholder={t(
                    'participants.list.customColumn.placeholder.title',
                  )}
                  name="title"
                  noBottomMargin
                  containerClass={classes.titleInput}
                />
                <Select
                  name="type"
                  label={t('participants.list.customColumn.form.type')}
                  placeholder={t(
                    'participants.list.customColumn.placeholder.type',
                  )}
                  options={columnsTypeList}
                  i18nKeyPrefix="participants.list.customColumn.columnTypes"
                  noMargin
                />
                <ErrorMsg name="type" />
                {(values.type === ColumnType.TAGS ||
                  values.type === ColumnType.SINGLE_CHOICE) && (
                  <Input
                    label={t('participants.list.customColumn.form.choices')}
                    placeholder={t(
                      'participants.list.customColumn.placeholder.choices',
                    )}
                    name="choices"
                    noMargin
                    containerClass={classes.choicesInput}
                  />
                )}

                <div className={classes.infoBox}>
                  <i
                    className={classnames(
                      'ri-information-line',
                      classes.infoIcon,
                    )}
                  ></i>
                  <Trans i18nKey="participants.list.customColumn.info" />
                </div>

                <div className={classes.submitButton}>
                  <SubmitButton small>
                    <Trans i18nKey="participants.list.customColumn.form.submit" />
                  </SubmitButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </CenteredModal>
    );
  },
);
